import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import Utils from 'src/app/helpers/utils';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';
import { ApiService } from 'src/app/services/api/api.service';
import UIDefaultTheme from 'src/themes';

@Component({
  selector: 'app-dashboard-approval-declarations',
  templateUrl: './dashboard-approval-declarations.component.html',
  styleUrls: ['./dashboard-approval-declarations.component.scss'],
})
export class DashboardApprovalDeclarationsComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() comparingVersions: boolean = false;
  approval: UserApprovalResponse;
  mainApproval: UserApprovalResponse;
  comparingApproval: UserApprovalResponse;
  data = null;
  mainData = null;
  comparingData = null;
  loading = true;
  mainLoading = false;
  comparingLoading = false;
  fullScreenLoading = false; // Spinner de tela cheia
  companyShortName = UIDefaultTheme.brand.name;

  pepData: CompanyComplementaryPepListValues[] = [];
  mainPepData: CompanyComplementaryPepListValues[] = [];
  comparingPepData: CompanyComplementaryPepListValues[] = [];
  pepColumns: TableColumnList<CompanyComplementaryPepListValues>[] = [];
  mainPepColumns: TableColumnList<CompanyComplementaryPepListValues>[] = [];
  comparingPepColumns: TableColumnList<CompanyComplementaryPepListValues>[] =
    [];

  qualificationOptions = [
    { label: 'Certificado', value: 'certified' },
    { label: 'Qualificado', value: 'qualified' },
    { label: 'Profissional', value: 'professional' },
  ];

  private eventMainVersion: any;
  private eventComparingVersion: any;

  constructor(
    private authService: AuthService,
    public approvalService: ApprovalService,
    private api: ApiService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private maskPipe: MaskPipe,
  ) {}

  ngOnInit(): void {
    // Colunas normais (sem comparação) - com formatação de documentos
    this.pepColumns = [
      {
        name: 'Nome',
        cell: (row) => {
          const formattedDoc = this.formatDocument(row.document_number);
          return `
            <span>${row.name}</span>
            <span class="app-table-details-body-row-subtitle">${formattedDoc}</span>
          `;
        },
      },
      { name: 'Vínculo', cell: (row) => row.bond },
    ];
    this.mainPepColumns = this.getPepColumnsForView('main');
    this.comparingPepColumns = this.getPepColumnsForView('comparing');

    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.complementary_data) {
        this.approval = this.approvalService.approvalData.complementary_data;
        this.setData();
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      },
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy() {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = null;
      this.mainApproval = null;
      this.mainPepData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = null;
      this.comparingApproval = null;
      this.comparingPepData = [];
      this.comparingLoading = false;
      return;
    }
    this.comparingLoading = true;
    this.getApprovalData('comparing');
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    try {
      // Ativar spinner de tela cheia apenas para a primeira carga (sem versionType)
      if (!versionType) {
        this.fullScreenLoading = true;
      }

      const register_uuid = this.route.snapshot.params.id;

      if (!register_uuid) {
        this.setLoadingState(versionType, false);
        if (!versionType) {
          this.fullScreenLoading = false;
        }
        return;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainData = null;
          this.mainApproval = null;
          this.mainPepData = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = null;
          this.comparingApproval = null;
          this.comparingPepData = [];
          this.comparingLoading = false;
        }
        return;
      }

      // Preparar parâmetros para API v3
      const params: any = {
        data_type: 'declaration_and_authorization',
      };

      // Verificar se há versão específica selecionada
      let versionToUse = null;
      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        versionToUse = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        versionToUse = this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        versionToUse = this.approvalService.approvalData.version;
      }

      if (versionToUse) {
        params.version = versionToUse;
      } else {
        params.last = true;
      }

      // Fazer requisição para API v3
      const response = await this.api.get<any>({
        route: `api/v3/registration/versions/register/${register_uuid}/`,
        params: params,
        token: true,
      });

      // Extrair dados do response v3
      const v3Data = response?.data;
      const declarationVersions =
        v3Data?.versions?.declaration_and_authorization?.versions;
      const declarationData = declarationVersions?.[0]?.data ?? null;

      // Criar um objeto mock de approval para manter compatibilidade com setData
      const mockApproval: any = {
        register: {
          declaration_authorization: declarationData ? [declarationData] : [],
        },
      };

      if (versionType === 'main') {
        this.mainApproval = mockApproval;
        this.setData(mockApproval, 'main');
        // Atualizar colunas após carregar dados
        this.mainPepColumns = this.getPepColumnsForView('main');
        this.mainLoading = false;
        this.cdr.detectChanges();
      } else if (versionType === 'comparing') {
        this.comparingApproval = mockApproval;
        this.setData(mockApproval, 'comparing');
        // Atualizar colunas após carregar dados
        this.comparingPepColumns = this.getPepColumnsForView('comparing');
        this.comparingLoading = false;
        this.cdr.detectChanges();
        // Aplicar estilos após carregar dados
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.applyComparisonStyles();
          });
        });
      } else {
        this.approval = mockApproval;
        this.approvalService.approvalData.complementary_data = mockApproval;
        this.setData(mockApproval);
        this.loading = false;
        this.fullScreenLoading = false; // Desativar spinner de tela cheia
      }
    } catch (error) {
      console.error('Erro ao buscar dados de declarações:', error);
      this.setLoadingState(versionType, false);
      if (!versionType) {
        this.fullScreenLoading = false; // Desativar spinner em caso de erro
      }
    }
  }

  /**
   * Helper para definir estado de loading
   */
  private setLoadingState(
    versionType: 'main' | 'comparing' | null,
    loading: boolean,
  ) {
    if (versionType === 'main') {
      this.mainLoading = loading;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingLoading = loading;
      this.cdr.detectChanges();
    } else {
      this.loading = loading;
    }
  }

  setData(
    approvalData?: UserApprovalResponse,
    versionType: 'main' | 'comparing' | null = null,
  ) {
    const approval = approvalData || this.approval;
    if (!approval) return;

    const declarationData = approval.register.declaration_authorization
      ? approval.register.declaration_authorization[0]
      : null;

    if (versionType === 'main') {
      this.mainData = declarationData;
      this.mainPepData = this.extractPepData(declarationData);
    } else if (versionType === 'comparing') {
      this.comparingData = declarationData;
      this.comparingPepData = this.extractPepData(declarationData);
    } else {
      this.data = declarationData;
      this.pepData = this.extractPepData(declarationData);
    }
  }

  extractPepData(declarationData: any): CompanyComplementaryPepListValues[] {
    if (
      !declarationData ||
      !declarationData.politically_exposed_person_relationship
    ) {
      return [];
    }

    let arr = [];
    if (declarationData.name && Array.isArray(declarationData.name)) {
      for (let i = 0; i < declarationData.name.length; i++) {
        arr.push({
          document_number: declarationData.document_number?.[i] || '',
          name: declarationData.name[i],
          bond: declarationData.bond?.[i] || '',
        });
      }
    }
    return arr;
  }

  getDataForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) {
      return this.data;
    }
    if (versionType === 'main') {
      return this.mainData;
    } else if (versionType === 'comparing') {
      return this.comparingData;
    }
    return null;
  }

  getPepDataForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) {
      return this.pepData;
    }
    if (versionType === 'main') {
      return this.mainPepData;
    } else if (versionType === 'comparing') {
      return this.comparingPepData;
    }
    return [];
  }

  getLoadingForView(versionType: 'main' | 'comparing' | null = null) {
    if (!this.comparingVersions) {
      return this.loading;
    }
    if (versionType === 'main') {
      return this.mainLoading;
    } else if (versionType === 'comparing') {
      return this.comparingLoading;
    }
    return false;
  }

  formatBrl(amount: number | string) {
    const formatted = Utils.formatBrl(Number(amount));

    return formatted;
  }

  renderLabel(slug: string) {
    const finded = this.qualificationOptions.find(
      (item) => item.value === slug,
    );

    return finded?.label ?? 'Público geral';
  }

  formatDocument(document: string): string {
    if (!document) return '';
    const cleanDoc = document.replace(/\D/g, '');
    if (cleanDoc.length === 11) {
      return this.maskPipe.transform(cleanDoc, '000.000.000-00');
    } else if (cleanDoc.length === 14) {
      return Utils.formatDocumentForDisplay(cleanDoc);
    }
    return document;
  }

  // Função helper para obter valor do main quando estiver no contexto comparing
  getMainValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.mainData) return null;

    const keys = path.split('.');
    let value: any = this.mainData;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  // Função helper para obter valor do comparing quando estiver no contexto comparing
  getComparingValueForComparison(path: string): any {
    if (!this.comparingVersions || !this.comparingData) return null;

    const keys = path.split('.');
    let value: any = this.comparingData;

    for (const key of keys) {
      if (value && value[key] !== undefined) {
        value = value[key];
      } else {
        return null;
      }
    }

    return value;
  }

  // Função para determinar o status de um campo (added, removed, modified, unchanged)
  getFieldStatus(path: string): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions) {
      return 'unchanged';
    }

    const mainValue = this.getMainValueForComparison(path);
    const comparingValue = this.getComparingValueForComparison(path);

    const normalizeValue = (val: any): string => {
      if (val === null || val === undefined || val === '') return '';
      if (typeof val === 'boolean') return val ? 'sim' : 'não';
      if (typeof val === 'string') {
        return val.toLowerCase().trim();
      }
      return String(val).toLowerCase().trim();
    };

    const mainNorm = normalizeValue(mainValue);
    const comparingNorm = normalizeValue(comparingValue);

    // Adicionado: esquerdo não tem, direito tem
    if (!mainNorm && comparingNorm) {
      return 'added';
    }

    // Removido: esquerdo tem, direito não tem
    if (mainNorm && !comparingNorm) {
      return 'removed';
    }

    // Modificado: ambos têm mas são diferentes
    if (mainNorm && comparingNorm && mainNorm !== comparingNorm) {
      return 'modified';
    }

    return 'unchanged';
  }

  // Função para obter o valor a ser exibido (se for removed, mostra o valor do main)
  getFieldValueForDisplay(
    path: string,
    currentValue: any,
    versionType: 'main' | 'comparing' | null = null,
  ): any {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return currentValue;
    }

    const status = this.getFieldStatus(path);
    if (status === 'removed') {
      // Se foi removido, mostrar o valor original do main
      return this.getMainValueForComparison(path);
    }

    return currentValue;
  }

  // Função para verificar se o campo deve ter strikethrough
  getFieldStrikethroughClass(
    path: string,
    versionType: 'main' | 'comparing' | null = null,
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getFieldStatus(path);
    if (status === 'removed') {
      return 'text-decoration-line-through';
    }

    return '';
  }

  // Função para retornar o ícone HTML baseado no status do campo
  getFieldComparisonIcon(
    path: string,
    versionType: 'main' | 'comparing' | null = null,
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getFieldStatus(path);

    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="error" />';
      case 'modified':
        return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="warning" />';
      default:
        return '';
    }
  }

  // Métodos para comparação de PEP data (tabela)
  getPepItemStatus(
    item: CompanyComplementaryPepListValues,
    versionType: 'main' | 'comparing' | null = null,
  ): 'added' | 'removed' | 'modified' | 'unchanged' {
    if (!this.comparingVersions) {
      return 'unchanged';
    }

    const mainItem = this.mainPepData.find(
      (p) => p.document_number === item.document_number || p.name === item.name,
    );
    const comparingItem = this.comparingPepData.find(
      (p) => p.document_number === item.document_number || p.name === item.name,
    );

    if (!mainItem && comparingItem) {
      return 'added';
    }

    if (mainItem && !comparingItem) {
      return 'removed';
    }

    if (mainItem && comparingItem) {
      if (
        mainItem.name !== comparingItem.name ||
        mainItem.document_number !== comparingItem.document_number ||
        mainItem.bond !== comparingItem.bond
      ) {
        return 'modified';
      }
    }

    return 'unchanged';
  }

  getPepItemRowClass(
    item: CompanyComplementaryPepListValues,
    versionType: 'main' | 'comparing' | null = null,
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getPepItemStatus(item, versionType);
    switch (status) {
      case 'modified':
        return 'row-compare-modified';
      case 'added':
        return 'row-compare-added';
      case 'removed':
        return 'row-compare-removed';
      default:
        return '';
    }
  }

  getPepItemIcon(
    item: CompanyComplementaryPepListValues,
    versionType: 'main' | 'comparing' | null = null,
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    const status = this.getPepItemStatus(item, versionType);
    switch (status) {
      case 'added':
        return '<img src="/assets/images/carbon-checkmark.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="check" />';
      case 'removed':
        return '<img src="/assets/images/carbon-error-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="error" />';
      default:
        return '';
    }
  }

  isPepFieldModified(
    item: CompanyComplementaryPepListValues,
    field: string,
    versionType: 'main' | 'comparing' | null = null,
  ): boolean {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return false;
    }

    const mainItem = this.mainPepData.find(
      (p) => p.document_number === item.document_number || p.name === item.name,
    );
    const comparingItem = this.comparingPepData.find(
      (p) => p.document_number === item.document_number || p.name === item.name,
    );

    if (!mainItem || !comparingItem) {
      return false;
    }

    return mainItem[field] !== comparingItem[field];
  }

  getPepFieldComparisonIcon(
    item: CompanyComplementaryPepListValues,
    field: string,
    versionType: 'main' | 'comparing' | null = null,
  ): string {
    if (versionType !== 'comparing' || !this.comparingVersions) {
      return '';
    }

    if (this.isPepFieldModified(item, field, versionType)) {
      return '<img src="/assets/images/carbon-warning.svg" class="comparison-icon" style="width: 16px; height: 16px; flex-shrink: 0; display: inline-block; vertical-align: middle; margin-right: 6px;" alt="warning" />';
    }

    return '';
  }

  getPepDataForComparison(
    versionType: 'main' | 'comparing' | null = null,
  ): CompanyComplementaryPepListValues[] {
    if (!this.comparingVersions) {
      return this.pepData;
    }

    if (versionType === 'main') {
      return this.mainPepData;
    } else if (versionType === 'comparing') {
      // Incluir itens removidos no lado direito
      const result = [...this.comparingPepData];
      const removedItems = this.mainPepData.filter(
        (mainItem) =>
          !this.comparingPepData.find(
            (compItem) =>
              compItem.document_number === mainItem.document_number ||
              compItem.name === mainItem.name,
          ),
      );
      return [...result, ...removedItems];
    }

    return [];
  }

  getPepColumnsForView(
    versionType: 'main' | 'comparing' | null = null,
  ): TableColumnList<CompanyComplementaryPepListValues>[] {
    const isComparing = versionType === 'comparing' && this.comparingVersions;
    const versionTypeForClosure = versionType;

    return [
      {
        name: 'Nome',
        cell: (row) => {
          const rowClass = isComparing
            ? this.getPepItemRowClass(row, versionTypeForClosure)
            : '';
          const iconHtml = isComparing
            ? this.getPepItemIcon(row, versionTypeForClosure)
            : '';
          const warningIcon = isComparing
            ? this.getPepFieldComparisonIcon(row, 'name', versionTypeForClosure)
            : '';
          const nameValue = isComparing
            ? this.getPepItemStatus(row, versionTypeForClosure) === 'removed'
              ? this.mainPepData.find(
                  (p) =>
                    p.document_number === row.document_number ||
                    p.name === row.name,
                )?.name || row.name
              : row.name
            : row.name;
          const isRemoved =
            isComparing &&
            this.getPepItemStatus(row, versionTypeForClosure) === 'removed';
          const textStyle = isRemoved ? 'text-decoration: line-through;' : '';

          const wrapperClass = rowClass
            ? `row-compare-wrapper ${rowClass}`
            : '';

          const formattedDoc = this.formatDocument(row.document_number);
          return `<div class="${wrapperClass}" style="display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; ${textStyle}">
                    ${iconHtml}
                    <div style="display: flex; flex-direction: column;">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        ${warningIcon}
                        <span style="${textStyle}">${nameValue}</span>
                      </div>
                      <span class="app-table-details-body-row-subtitle" style="${textStyle}">${formattedDoc}</span>
                    </div>
                  </div>`;
        },
      },
      {
        name: 'Vínculo',
        cell: (row) => {
          const warningIcon = isComparing
            ? this.getPepFieldComparisonIcon(row, 'bond', versionTypeForClosure)
            : '';
          const bondValue = isComparing
            ? this.getPepItemStatus(row, versionTypeForClosure) === 'removed'
              ? this.mainPepData.find(
                  (p) =>
                    p.document_number === row.document_number ||
                    p.name === row.name,
                )?.bond || row.bond
              : row.bond
            : row.bond;
          const isRemoved =
            isComparing &&
            this.getPepItemStatus(row, versionTypeForClosure) === 'removed';
          const textStyle = isRemoved ? 'text-decoration: line-through;' : '';

          return `
            <div style="display: flex; align-items: center; gap: 6px; flex-direction: row;">
              ${warningIcon}
              <span style="${textStyle}">${bondValue}</span>
            </div>
          `;
        },
      },
    ];
  }

  getPepColumnsForViewHelper(
    versionType: 'main' | 'comparing' | null = null,
  ): TableColumnList<CompanyComplementaryPepListValues>[] {
    if (!this.comparingVersions) {
      return this.pepColumns;
    }
    if (versionType === 'main') {
      return this.mainPepColumns;
    } else if (versionType === 'comparing') {
      return this.comparingPepColumns;
    }
    return this.pepColumns;
  }

  ngAfterViewInit(): void {
    if (this.comparingVersions) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.applyComparisonStyles();
        });
      });
    }
  }

  applyComparisonStyles() {
    if (!this.comparingVersions) return;

    // Aplicar estilos apenas no lado direito (comparing)
    // Encontrar todas as tabelas de comparação
    const comparingTables = this.el.nativeElement.querySelectorAll(
      '.col-6:last-child .app-table-details-body',
    );

    // Limpar classes anteriores das linhas do lado direito
    comparingTables.forEach((row: any) => {
      row.classList.remove(
        'row-compare-modified',
        'row-compare-added',
        'row-compare-removed',
      );
    });

    // Aplicar estilos às linhas da tabela do lado direito
    comparingTables.forEach((row: any) => {
      const cells = row.querySelectorAll('.app-table-details-body-row');
      if (cells.length > 0) {
        // Verificar a primeira célula que geralmente contém o wrapper
        const firstCell = cells[0];
        if (firstCell) {
          const wrapper = firstCell.querySelector('.row-compare-wrapper');
          if (wrapper) {
            if (wrapper.classList.contains('row-compare-modified')) {
              row.classList.add('row-compare-modified');
            } else if (wrapper.classList.contains('row-compare-added')) {
              row.classList.add('row-compare-added');
            } else if (wrapper.classList.contains('row-compare-removed')) {
              row.classList.add('row-compare-removed');
            }
          } else {
            // Se não encontrou wrapper, verificar diretamente no innerHTML
            const innerHTML = firstCell.innerHTML || '';
            if (innerHTML.includes('row-compare-modified')) {
              row.classList.add('row-compare-modified');
            } else if (innerHTML.includes('row-compare-added')) {
              row.classList.add('row-compare-added');
            } else if (innerHTML.includes('row-compare-removed')) {
              row.classList.add('row-compare-removed');
            }
          }
        }
      }
    });
  }
}
